import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import TextFieldData from "./TextFiledData"; // Fixed import name
import { Box, Button } from "@mui/material";
import EastIcon from "@mui/icons-material/East";
import MainDashboardLayout from "../../../Layout/MainDashboardLayout";
import GRISidebar from "../../../Layout/GRISidebar";
import { put, get } from "../../../utils/api";
import ReviewBox from "../../../Components/Common/ReviewBox";
import { currentYear } from "../../../utils/generateYearRanges";

const Economic221 = ({ hide }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [disabled, setDisabled] = useState(false);
  const [errors, setErrors] = useState({});
  const [griReview, setGriReview] = useState(() => JSON.parse(localStorage.getItem("griReview")));

  const [fromData, setFromData] = useState({
    medianEmployeeCompensationRatio: {
      annual: "",
      allEmployees: "",
      ration: "",
    },
    highestPaidCompensationIncreaseRatio: {
      topExecutive: "",
      mediumPercentage: "",
      ration: "",
    },
    payRatioCalculationMethodology: "",
    additionalContextualInformation: "",
  });

  const fetchData = async () => {
    const safeValue = (val) => val === 0 ? 0 : val || "";

  const calculateRatio = (firstValue, secondValue) => {
    if (firstValue === "" || secondValue === "") return "";

    const firstNumber = Number(firstValue);
    const secondNumber = Number(secondValue);

    if (Number.isNaN(firstNumber) || Number.isNaN(secondNumber)) return "";
    if (secondNumber === 0) return "N/A";

    const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
    const divisor = gcd(Math.abs(firstNumber), Math.abs(secondNumber));

    return `${firstNumber / divisor}:${secondNumber / divisor}`;
  };

  const getSavedOrCalculatedRatio = (savedRatio, firstValue, secondValue) =>
    safeValue(savedRatio) || calculateRatio(firstValue, secondValue);
    await get(`/gri/get?type=GeneralDisclosures2021&Id=${id}`)
      .then((res) => {
        if (res?.data?.data?.year ? res?.data?.data?.year < currentYear -1 : false)
          setDisabled(true);
        const annulTotalCompensation =
          res.data.data.GeneralDisclosures2021.annulTotalCompensation;
        const medianEmployeeCompensationRatio =
          annulTotalCompensation.medianEmployeeCompensationRatio;
        const highestPaidCompensationIncreaseRatio =
          annulTotalCompensation.highestPaidCompensationIncreaseRatio;
        const annual = safeValue(medianEmployeeCompensationRatio.annual);
        const allEmployees = safeValue(
          medianEmployeeCompensationRatio.allEmployees
        );
        const topExecutive = safeValue(
          highestPaidCompensationIncreaseRatio.topExecutive
        );
        const mediumPercentage = safeValue(
          highestPaidCompensationIncreaseRatio.mediumPercentage
        );

        setFromData({
          medianEmployeeCompensationRatio: {
            annual,
            allEmployees,
            ration: getSavedOrCalculatedRatio(medianEmployeeCompensationRatio.ration, annual, allEmployees),
          },
          highestPaidCompensationIncreaseRatio: {
            topExecutive,
            mediumPercentage,
            ration: getSavedOrCalculatedRatio(highestPaidCompensationIncreaseRatio.ration, topExecutive, mediumPercentage),
          },
          payRatioCalculationMethodology:
          safeValue(annulTotalCompensation.payRatioCalculationMethodology),
          additionalContextualInformation:
          safeValue(annulTotalCompensation.additionalContextualInformation),
        });
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Function to validate the form fields
  const validateFields = () => {
    const newErrors = {};

    // Loop through the 'fromData' object and validate
    Object.keys(fromData).forEach((sectionKey) => {
      const section = fromData[sectionKey];

      // If section is an object, loop through the fields inside it
      if (typeof section === "object") {
        Object.keys(section).forEach((fieldKey) => {
          if (!section[fieldKey]) {
            // If the field is empty, mark it as an error
            if (!newErrors[sectionKey]) {
              newErrors[sectionKey] = {};
            }
            newErrors[`${sectionKey}.${fieldKey}`] = `This field is required`;
          }
        });
      } else if (!section) {
        // If section is not an object, just check the value
        newErrors[sectionKey] = `This field is required`;
      }
    });
    console.log(newErrors);
    setErrors(newErrors);
    return newErrors;
  };

  // Function to handle form submission
  const handleSubmit = async () => {
    // const validationErrors = validateFields();

    // If there are errors, prevent form submission
    // if (Object.keys(validationErrors).length > 0) {
    //   console.log("Form contains errors", fromData, validationErrors);
    //   return;
    // }
    if (!disabled) {
      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          throw new Error("Authentication token is missing.");
        }
        await put(`/gri/update?Id=${id}`, {
          type: "GeneralDisclosures2021",
          annulTotalCompensation: { ...fromData },
          submitted2_21: true,
        })
          .then((res) => navigate(`/gri/general-disclosure/2-22/${id}`))
          .catch((err) => console.log(err));
      } catch {
        console.log(errors);
      }
    } else {
      navigate(`/gri/general-disclosure/2-22/${id}`);
    }
  };

  const inputData = [
    {
      index: "a.",
      heading:
        "Report the ratio of the annual total compensation for the organization's highest-paid individual to the median annual total compensation for all employees (excluding the highest-paid individual)",
      type: "flex",
      subfieldName: "highestPaidCompensationIncreaseRatio",

      fields: [
        {
          subheading: "Highest-paid individual's annual compensation",
          fieldName: "topExecutive",
        },
        {
          subheading:
            "Median annual total compensation for all employees, excluding the highest earner",
          fieldName: "mediumPercentage",
        },
        { subheading: "Ratio", fieldName: "ration" },
      ],
    },
    {
      index: "b.",
      heading:
        "Report the ratio of the percentage increase in annual total compensation for the organization's highest-paid individual to the median percentage increase in annual total compensation for all employees (excluding the highest-paid individual)",
      type: "flex",
      subfieldName: "medianEmployeeCompensationRatio",
      fields: [
        {
          subheading: "Percentage increase in top executive compensation",
          fieldName: "annual",
        },
        {
          subheading:
            "Median percentage increase in annual total compensation for all employees, excluding the highest earner",
          fieldName: "allEmployees",
        },
        { subheading: "Ratio", fieldName: "ration" },
      ],
    },
    {
      index: "c.",
      heading:
        "Describe the methodology used to calculate the pay ratio and pay increase ratio.",
      fieldName: "payRatioCalculationMethodology",
    },
    {
      index: "d.",
      heading:
        "Is there any additional contextual information that helps to understand the reported ratios (like Whether full-time equivalent (FTE) pay rates are used for each part-time employee, A list of the types of compensation included, The title of the highest-paid individual). ",
      fieldName: "additionalContextualInformation",
    },
  ];

  const renderContent = ({ hide }) => (
    <>
      <TextFieldData
        title="2-21 Annual total compensation ratio"
        {...{
          inputData,
          fromData,
          setFromData,
          disabled: disabled,
          setErrors,
          errors,
          hide
        }}
      />
    </>
  );

  
  return (
    <>
      {!hide ? (
        <MainDashboardLayout sidebar={<GRISidebar />}>
          {renderContent({ hide })}

          <>
            {!griReview ? (
              <Box sx={{ ml: "1rem", width: "98%" }}>
                <ReviewBox url={"/gri"} handleSubmit={handleSubmit} />
              </Box>
            ) : (
              <Box textAlign="right" sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "#1C486B",
                    textTransform: "none",
                    mt: 3,
                    mb: 5,
                  }}
                  onClick={handleSubmit}
                >
                  Save & Continue <EastIcon sx={{ ml: 2 }} />
                </Button>
              </Box>
            )}
          </>
        </MainDashboardLayout>
      ) : (
        renderContent({ hide })
      )}
    </>
  );
};

export default Economic221;


